import React from 'react';
import DelayedSlickSlider from '../giveinkind/components/animations/DelayedSlickSlider';
import Button, { ButtonStyle } from '../giveinkind/components/Button';
import Modal from '@gik/ui/Modal';
import User from '../../models/user-profile/User';
import UserProfileAPI from '../../api/UserProfileAPI';
import FadedBounce from '../giveinkind/components/animations/FadedBounce';
import IconButton, { IconButtonStyle } from '../giveinkind/components/IconButton';
import moment from 'moment';
import { AnimationDirectionsType } from '../giveinkind/components/animations/DelayedSwipe';
import { isInkindPageNew } from '../../utils/InkindPageComparer';
import UserSurvey from './UserSurvey';
import { SelectOptionsType } from '../giveinkind/components/Select';
import { Analytics } from '@gik/core/utils';

export interface IWelcomeScreenProps {
  user: User;
  created: moment.Moment;
}

export default function WelcomeScreen({ user, created }: IWelcomeScreenProps): JSX.Element {
  const slickSliderClassName: string = 'welcome-screen-slick-slider';
  const delayMs: number = 50;
  const animationsDurationMs: number = 600;

  const [isOpen, setOpen] = React.useState<boolean>(false);
  const [timeoutRef, setTimeoutRef] = React.useState<NodeJS.Timeout>(null);
  const [nextSlide, setNextSlide] = React.useState<number>(0);
  const [animationDirection, setAnimationDirection] = React.useState<AnimationDirectionsType>('left');
  const [pageSwitchDelayMs, setPageSwitchDelayMs] = React.useState<number>(0);
  const [feedback, setFeedback] = React.useState<SelectOptionsType>(null);

  const [isFadedBounce1Enabled, setFadedBounce1Enabled] = React.useState<boolean>(null);
  const [isFadedBounce2Enabled, setFadedBounce2Enabled] = React.useState<boolean>(null);
  const [isFadedBounce3Enabled, setFadedBounce3Enabled] = React.useState<boolean>(null);
  const [isFadedBounce4Enabled, setFadedBounce4Enabled] = React.useState<boolean>(null);
  const [isFadedBounce5Enabled, setFadedBounce5Enabled] = React.useState<boolean>(null);

  const delayedSlickSliderRef = React.useRef(null);

  const isNewPage = (): boolean => {
    return isInkindPageNew(created);
  };

  const hasUserSeenAnyWelcomeScreen = (): boolean => {
    return user.getWelcomeScreenLastSeen() !== null;
  };

  const hasUserSeenWelcomeScreenOnThisPage = (): boolean => {
    return (
      hasUserSeenAnyWelcomeScreen() &&
      user.getWelcomeScreenLastSeen() !== null &&
      user.getWelcomeScreenLastSeen().isValid() &&
      user.getWelcomeScreenLastSeen().isAfter(created)
    );
  };

  const shouldShowDialog =
    (isNewPage() && !hasUserSeenWelcomeScreenOnThisPage()) ||
    (!hasUserSeenAnyWelcomeScreen() && !hasUserSeenWelcomeScreenOnThisPage());

  React.useEffect(() => {
    if (timeoutRef === null) {
      setTimeoutRef(
        setTimeout(() => {
          if (shouldShowDialog) {
            setOpen(true);
          }
        }, 500)
      );
    }
  }, [shouldShowDialog, timeoutRef]);

  const onClose = function () {
    setOpen(false);
    markSeenWelcomeScreen();
    if (feedback) {
      submitSurveyAnswer(feedback.value);
    }
    Analytics.fireEvent('WelcomeScreenGotItClicked', {});
  };

  const onNext = function () {
    delayedSlickSliderRef.current && delayedSlickSliderRef.current.slickNext();
  };

  const onPrev = function () {
    delayedSlickSliderRef.current && delayedSlickSliderRef.current.slickPrev();
  };

  const onSkip = function () {
    setOpen(false);
    markSeenWelcomeScreen();
  };

  const submitSurveyAnswer = function (feedback: string) {
    if (user.getFoundOutAboutGIK() === null) {
      const api: UserProfileAPI = new UserProfileAPI();
      api.submitSurveyResults(user, feedback).catch(e => console.error(e));
    }
  };

  const markSeenWelcomeScreen = function () {
    const api: UserProfileAPI = new UserProfileAPI();
    api.updateUserLastSeenWelcomeScreen(user).catch(e => console.error(e));
  };

  const shouldShowAlternateCopy = function (): boolean {
    return !isNewPage() && !hasUserSeenAnyWelcomeScreen() && !hasUserSeenWelcomeScreenOnThisPage();
  };

  const shouldShowSkipButton = function (): boolean {
    return hasUserSeenAnyWelcomeScreen();
  };

  const shouldShowSurvey = function (): boolean {
    return user.getFoundOutAboutGIK() === null;
  };

  const beforeChange = (currentSlide: number, nextSlide: number, pageSwitchDelayMs: number): void => {
    const getFadedBounceState = (previousSlide: number, nextSlide: number, compareSlide: number): boolean => {
      return nextSlide == compareSlide ? true : previousSlide == compareSlide ? false : null;
    };

    if (currentSlide != nextSlide) {
      setPageSwitchDelayMs(pageSwitchDelayMs);
      setNextSlide(nextSlide);

      setFadedBounce1Enabled(getFadedBounceState(currentSlide, nextSlide, 1));
      setFadedBounce2Enabled(getFadedBounceState(currentSlide, nextSlide, 2));
      setFadedBounce3Enabled(getFadedBounceState(currentSlide, nextSlide, 3));
      setFadedBounce4Enabled(getFadedBounceState(currentSlide, nextSlide, 4));
      setFadedBounce5Enabled(getFadedBounceState(currentSlide, nextSlide, 5));

      if (currentSlide < nextSlide) {
        setAnimationDirection('left');
      } else if (currentSlide > nextSlide) {
        setAnimationDirection('right');
      }
    }
  };

  const getButton = (slide: number): JSX.Element => {
    if (slide == 5) {
      return <Button className="button-skip-save save" text="Got It!" style={ButtonStyle.Save} onClick={onClose} />;
    } else {
      return <Button className="button-skip-save skip" text="Next" style={ButtonStyle.Save} onClick={onNext} />;
    }
  };

  const bounceDelay = (): number => pageSwitchDelayMs + animationsDurationMs + 100;

  const header: JSX.Element = (
    <header className="welcome-screen-header">
      <span>{shouldShowAlternateCopy() ? <>Tips for Your Page</> : <>Welcome to Your Page</>}</span>

      {shouldShowSkipButton() && <IconButton onClick={onSkip} icon="close" style={IconButtonStyle.White} />}
    </header>
  );

  const handleChange = (selectedOption: SelectOptionsType) => {
    if (selectedOption) {
      setFeedback(selectedOption);
    }
  };

  const slidePages: JSX.Element[][] = [
    [
      <section className="slide-1 slide-image">
        <div className="welcome-intro-container">
          {shouldShowAlternateCopy() ? (
            <>
              <div className="welcome-intro">We're here to help!</div>
              <div className="welcome-subtitle">Here are a few tips for using our features</div>
            </>
          ) : (
            <>
              <div className="welcome-intro">You're almost there!</div>
              <div className="welcome-subtitle">Just a few tips to get you set up right</div>
            </>
          )}
        </div>
      </section>,
      <section className="slide-1 slide-copy">
        <p className="explanatory-text-default">
          <span className="explanatory-bold">
            <span className="explanatory-underline">Only you</span> as a page organizer
          </span>{' '}
          can see and use the{' '}
          <span className="explanatory-bold">
            <span className="organizer-special">orange</span> edit buttons
          </span>{' '}
          scattered around your page.
        </p>
        <p className="explanatory-text-secondary explanatory-italic">Here's what they do…</p>
      </section>,
    ],
    [
      <section className="slide-2 slide-image" />,
      <section className="slide-2 slide-copy">
        <p className="explanatory-text-title">Add Meals & Help to the Calendar</p>
        <p className="explanatory-text-secondary">
          Items become ready to claim by others. <span className="explanatory-italic explanatory-bold">Remember</span>,
          only you as an organizer have the ability to add items to your calendar, not your page visitors.
        </p>
      </section>,
    ],
    [
      <section className="slide-3 slide-image" />,
      <section className="slide-3 slide-copy">
        <p className="explanatory-text-title">Share Updates with Page Supporters</p>
        <p className="explanatory-text-secondary">
          Keep all your supporters up-to-date with news, photos or videos, wherever they are.
        </p>
      </section>,
    ],
    [
      <section className="slide-4 slide-image" />,
      <section className="slide-4 slide-copy">
        <p className="explanatory-text-title">Add Products to Your Wishlist</p>
        <p className="explanatory-text-secondary">
          Browse our hand-picked items in our GIVE section, and add (+) them to your wishlist.
        </p>
      </section>,
    ],
    [
      <section className="slide-5 slide-image" />,
      <section className="slide-5 slide-copy">
        <p className="explanatory-text-title">Customize Your Page</p>
        <p className="explanatory-text-secondary">
          Tell supporters why you started the page, add special notes, turn on/off features & privacy preferences, add
          organizers—<span className="explanatory-italic">let others know how best to help.</span>
        </p>
      </section>,
    ],
    [
      <section className="slide-6 slide-image" />,
      <section className="slide-6 slide-copy">
        <div className="welcome-outro">That's It!</div>
        <p className="explanatory-text-secondary">
          If you need help, just tap our support button on the bottom right of your screen. We’re here to help!
        </p>
        <div className="intercom-icon" />
      </section>,
    ],
  ];

  const prevArrow = (
    <>
      {nextSlide > 0 && (
        <div className="welcome-screen-control-button control-button-prev">
          <IconButton style={IconButtonStyle.White} onClick={onPrev} icon="chevronLeft" />
        </div>
      )}
    </>
  );

  const nextArrow = (
    <>
      {nextSlide < slidePages.length - 1 && (
        <div className="welcome-screen-control-button control-button-next">
          <IconButton style={IconButtonStyle.White} onClick={onNext} icon="chevronRight" />
        </div>
      )}
    </>
  );

  return (
    <Modal
      className="welcome-screen"
      shouldCloseOnEsc={shouldShowSkipButton()}
      shouldCloseOnBack={shouldShowSkipButton()}
      header={header}
      isOpen={isOpen}
      footer={getButton(nextSlide)}
      overlay={
        <>
          {prevArrow}
          {nextArrow}
        </>
      }
      pages={[
        {
          title: '',
          content: (
            <>
              <div className="slick-slider-wrapper">
                <DelayedSlickSlider
                  ref={delayedSlickSliderRef}
                  slidePages={slidePages}
                  delayMs={delayMs}
                  animationsDurationMs={animationsDurationMs}
                  slickSliderClassName={slickSliderClassName}
                  beforeChange={beforeChange}
                  animationDirection={animationDirection}
                />
              </div>

              <div className="slide-2-buttons">
                <FadedBounce enabled={isFadedBounce1Enabled} delay={bounceDelay()}>
                  <div className="buttons-container">
                    <div className="add-mobile-image" /> or <div className="add-desktop-image" />
                  </div>
                </FadedBounce>
              </div>

              <div className="slide-3-buttons">
                <FadedBounce enabled={isFadedBounce2Enabled} delay={bounceDelay()}>
                  <div className="buttons-container">
                    <div className="add-update-image" />
                  </div>
                </FadedBounce>
              </div>

              <div className="slide-4-buttons">
                <FadedBounce enabled={isFadedBounce3Enabled} delay={bounceDelay()}>
                  <div className="buttons-container">
                    <div className="add-wishlist-image" />
                  </div>
                </FadedBounce>
              </div>

              <div className="slide-5-buttons">
                <FadedBounce enabled={isFadedBounce4Enabled} delay={bounceDelay()}>
                  <div className="buttons-container">
                    <div>
                      <div className="edit-image" />
                      <div className="photo-image" />
                      <div className="background-image" />
                    </div>
                    <div className="page-settings-image" />
                  </div>
                </FadedBounce>
              </div>

              {shouldShowSurvey() && (
                <div className="slide-6-survey">
                  <FadedBounce enabled={isFadedBounce5Enabled} delay={bounceDelay()}>
                    <div>
                      <UserSurvey feedback={feedback} onChange={handleChange} />
                    </div>
                  </FadedBounce>
                </div>
              )}
            </>
          ),
        },
      ]}
      pageNumber={1}
      onClose={onClose}
    />
  );
}
